/* eslint-disable @typescript-eslint/member-delimiter-style */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'
import { LineChart, Text } from '@tremor/react'
import { CircularProgress, IconButton } from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'

import moment from 'config/moment'
import { useApiCall } from 'hooks/useApi'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import {
  getWelcomeRewardUses,
  WelcomeRewardUsesParams,
  WelcomeRewardUsesResponse,
  WelcomeRewardUsesData,
} from './api'

interface Store {
  id: number
  name: string
}

interface WelcomeRewardUsesProps {
  period: { month: number; year: number }
  storeIds?: number[]
  stores?: Store[]
  trigger: number
  compara: boolean
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

const lineColors = ['green', 'emerald']
const montserratStyle = { fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }

const WelcomeRewardUses: React.FC<WelcomeRewardUsesProps> = ({
  period,
  storeIds,
  stores,
  trigger,
  compara,
  onLoaded,
}) => {
  const [data, setData] = useState<{ [storeId: number]: WelcomeRewardUsesData[] }>({})
  const [error, setError] = useState<string | null>(null)
  const [isLoadingData, setIsLoadingData] = useState(false)

  const [apiCall, apiLoading] = useApiCall<WelcomeRewardUsesParams, WelcomeRewardUsesResponse>(
    getWelcomeRewardUses,
  )

  // Track loading state
  useLoadingTracker({ isLoading: apiLoading, componentId: 'WelcomeRewardUses' })

  const fetchData = async (storeId?: number) => {
    try {
      const endDate = moment()
        .year(period.year)
        .month(period.month)
        .endOf('month')
        .format('YYYY-MM-DD')

      const response = await apiCall({
        endDate,
        storeId,
      })

      if (response?.data) {
        setData(prev => ({
          ...prev,
          [storeId || 0]: response.data,
        }))
      }
    } catch (apiError) {
      console.error('Error fetching welcome reward uses data:', apiError)
      setError('Error al cargar datos de uso de rewards de bienvenida')
    }
  }

  const fetchAllData = async () => {
    try {
      setIsLoadingData(true)
      setError(null)
      setData({})

      if (storeIds && storeIds.length > 0) {
        if (storeIds.length === 1 && compara) {
          await Promise.all([fetchData(undefined), fetchData(storeIds[0])])
        } else {
          // Fetch data for each store in parallel
          await Promise.all(storeIds.map(storeId => fetchData(storeId)))
        }
      } else {
        // Fetch global data (no store filter)
        await fetchData()
      }
    } catch (fetchError) {
      console.error('Error fetching all data:', fetchError)
      setError('Error al cargar datos')
    } finally {
      setIsLoadingData(false)
      if (onLoaded) {
        onLoaded()
      }
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      fetchAllData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  const getStoreName = (storeId: string) => {
    if (storeId === '0') return 'Todos los locales'
    const store = stores?.find(s => s.id === parseInt(storeId, 10))
    return store?.name || `Store ${storeId}`
  }

  const formatChartData = () => {
    const allMonths = new Set<string>()

    // Collect all unique months from all stores
    Object.values(data).forEach(storeData => {
      storeData.forEach(item => allMonths.add(item.month))
    })

    const sortedMonths = Array.from(allMonths).sort()

    return sortedMonths.map(month => {
      const chartData: any = { month: moment(month, 'YYYY-MM').format('MMMM') }

      Object.entries(data).forEach(([storeId, storeData]) => {
        const monthData = storeData.find(item => item.month === month)
        const storeName = getStoreName(storeId)
        if (monthData) {
          chartData[storeName] = parseFloat(monthData.percentage.toFixed(2))
        } else {
          chartData[storeName] = 0
        }
      })

      return chartData
    })
  }

  const calculateSummaryStats = () => {
    const allData = Object.values(data).flat()
    if (allData.length === 0) return { current: 0, previous: 0 }

    // Get the most recent month data
    const sortedData = allData.sort((a, b) =>
      moment(b.month, 'YYYY-MM').diff(moment(a.month, 'YYYY-MM')),
    )
    const current = sortedData[0]
    const previous = sortedData[1]

    return {
      current: current?.percentage || 0,
      previous: previous?.percentage || 0,
    }
  }

  const calculatePercentageChange = () => {
    const { current, previous } = calculateSummaryStats()
    if (previous === 0) return 0
    return parseFloat((current - previous).toFixed(1))
  }

  const percentageChange = calculatePercentageChange()
  const isPositive = percentageChange >= 0
  const { current: currentPercentage } = calculateSummaryStats()

  // TODO pasar a otro service
  const handleDownload = () => {
    if (Object.keys(data).length === 0) return

    const csvContent = [
      'Mes,Store,Porcentaje,Total registros,Total beneficios consumidos',
      ...Object.entries(data).flatMap(([storeId, storeData]) =>
        storeData.map(item => {
          const monthLabel = moment(item.month, 'YYYY-MM').format('MMMM YYYY')
          const storeName = getStoreName(storeId)
          const percentage = item.percentage.toFixed(2)
          const { totalRegisteredUsers } = item
          const { totalConsumedWelcomeRewards } = item
          return `${monthLabel},${storeName},${percentage}%,${totalRegisteredUsers},${totalConsumedWelcomeRewards}`
        }),
      ),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `welcome-reward-uses-${period.year}-${period.month + 1}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const chartData = formatChartData()
  const storeNames = Object.keys(data).map(storeId => getStoreName(storeId))
  const isLoading = apiLoading || isLoadingData

  const renderChartContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <CircularProgress size={40} />
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-red-500 text-center" style={montserratStyle}>
            {error}
          </div>
        </div>
      )
    }

    if (Object.keys(data).length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <Text className="text-center">
            No hay datos disponibles para mostrar en este momento.
          </Text>
        </div>
      )
    }

    return (
      <LineChart
        data={chartData}
        index="month"
        categories={storeNames}
        colors={lineColors.slice(0, storeNames.length)}
        valueFormatter={v => `${v}%`}
        yAxisWidth={40}
        showLegend={storeNames.length > 1}
        showAnimation
        showGridLines
        className="font-sans"
        style={montserratStyle}
      />
    )
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="font-bold text-base block mb-2" style={montserratStyle}>
            Conversión - Uso de Reward de bienvenida
          </span>

          {!isLoading && Object.keys(data).length > 0 && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-3xl font-extrabold" style={montserratStyle}>
                  {currentPercentage.toFixed(2)}%
                </span>
                {percentageChange !== 0 && compara && (
                  <span
                    className={`font-bold rounded-lg px-2 py-1 flex items-center text-base ${
                      isPositive ? 'bg-green-100' : 'bg-red-100'
                    }`}
                    style={montserratStyle}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={isPositive ? '#47B960' : '#EF4444'}
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-1"
                      style={{ transform: isPositive ? 'none' : 'rotate(180deg)' }}
                    >
                      <path d="M6 15l6-6 6 6" />
                    </svg>
                    {Math.abs(percentageChange)}%
                  </span>
                )}
                {/* <span className="text-gray-600 text-base" style={montserratStyle}>
                  (comparado con el mes anterior)
                </span> */}
              </div>
            </>
          )}
        </div>
        <IconButton
          disabled={isLoading || Object.keys(data).length === 0}
          className="ml-2"
          aria-label="download"
          onClick={handleDownload}
        >
          <GetAppIcon />
        </IconButton>
      </div>

      <div className="w-full h-[350px]">{renderChartContent()}</div>
    </div>
  )
}

WelcomeRewardUses.defaultProps = {
  storeIds: [],
  stores: [],
}

export default WelcomeRewardUses
